import { PayableRow, PayableStatus } from "@wingspanhq/wingspan-api-sdk";
import { WSQueryCache } from "@ws-react-query";
import { payablesApi } from "@api";
import { queriesPayableRows, queriesPayables } from "@query";
import {
  BulkActionOnProgress,
  BulkActionProgress,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../BulkActions";

const CANCELLABLE_STATUSES: ReadonlySet<PayableStatus> = new Set<PayableStatus>(
  [PayableStatus.OPENED, PayableStatus.PENDING]
);

const invalidateAfterMutation = (successItems: PayableRow[]) => {
  WSQueryCache.invalidateQueries(
    queriesPayableRows.searchPayableRows.queryKeyBase
  );
  WSQueryCache.invalidateQueries(queriesPayables.getPayable.queryKeyBase);
};

const processCancelPayables = (
  items: PayableRow[],
  onProgress: BulkActionOnProgress,
  onSuccess?: () => void
): Promise<BulkActionProgress> => {
  const bulkAction = bulkActionFactory<PayableRow>({
    onProgress,
    action: async item => {
      if (!item.id) {
        throw new Error("Payable is missing id");
      }
      if (!item.status || !CANCELLABLE_STATUSES.has(item.status)) {
        throw new Error(
          `Cannot cancel payable with status "${item.status}". Only open and pending payables can be cancelled.`
        );
      }
      return payablesApi.cancelPayable({ payableId: item.id });
    },
    onFinish: ({ successItems }) => {
      invalidateAfterMutation(successItems);
      onSuccess?.();
    }
  });

  return bulkAction.run(items).then(({ successItems, failedItems }) => ({
    total: items.length,
    processed: successItems.length + failedItems.length,
    successful: successItems.length,
    failed: failedItems.length
  }));
};

export const useBulkCancelPayables = () => {
  const runBulkAction = useForegroundBulkAction();

  return (items: PayableRow[], onSuccess?: () => void) => {
    const count = items.length;
    const pluralized = count > 1 ? "payables" : "payable";

    return runBulkAction({
      actionName: "Cancel payables",
      itemCount: count,
      confirmDescription: `Are you sure you want to cancel ${count.toLocaleString()} ${pluralized}? Cancelled payables will not be paid.`,
      confirmLabel: "Cancel payables",
      confirmDestructive: true,
      prepare: async () => items,
      process: async (data, onProgress) =>
        processCancelPayables(data, onProgress, onSuccess)
    });
  };
};
